import React, { useState, useEffect } from 'react';
import { Button } from '@shadcn/components/ui/button';
import { Badge } from '@shadcn/components/ui/badge';
import { X, Loader2 } from 'lucide-react';
import moment from 'moment';
import automationApi from '../../services/automationApi';
import useUserStore from '../../../../../zustand/useUserStore';

const VersionHistory = ({ workflowId, activeVersionId, onSelectVersion, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const userList = useUserStore((state) => state.userList);
  const fetchUserList = useUserStore((state) => state.fetchUserList);

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  const getUser = (userId) => {
    if (!userId || !userList) return null;
    return userList.find((u) => u._id === userId) || null;
  };

  useEffect(() => {
    const fetchVersions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await automationApi.getWorkflowVersions(workflowId);
        setVersions(result.data || result.versions || result || []);
      } catch (err) {
        setError(err.message || 'Failed to load versions');
      } finally {
        setIsLoading(false);
      }
    };

    if (workflowId) {
      fetchVersions();
    }
  }, [workflowId]);

  return (
    <div style={{ flexBasis: '23%', maxWidth: '23%' }} className="shrink-0 grow-0 bg-white border-l overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-medium text-sm">Version History</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X size={14} />
        </Button>
      </div>

      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-500 text-center py-8">{error}</p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No versions found</p>
        ) : (
          <div className="space-y-3">
            {versions.map((version, index) => {
              const versionId = version._id || version.version;
              const isSelected = activeVersionId === versionId;
              const isCurrent = index === 0;

              return (
                <button
                  key={versionId ?? index}
                  onClick={() => onSelectVersion(version, index)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">
                      Version {versions.length - index}
                    </span>
                    {isCurrent && !activeVersionId && (
                      <Badge variant="secondary" className="text-xs">Current</Badge>
                    )}
                    {isSelected && (
                      <Badge className="text-xs bg-blue-100 text-blue-800">Viewing</Badge>
                    )}
                  </div>
                  {version.createdAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      <span className="text-gray-500">Last edited:</span> {moment(version.createdAt).format('D MMM YYYY, h:mm A')}
                    </p>
                  )}
                  {version.updatedBy && (() => {
                    const user = getUser(version.updatedBy);
                    return (
                      <p className="text-xs text-gray-400 mt-1">
                        <span className="text-gray-500">Edited by:</span> {user ? user.email : ''}
                      </p>
                    );
                  })()}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VersionHistory;
